import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tooltip } from "primereact/tooltip";
import { Button } from "react-bootstrap";

const normalizeVoices = (raw) => {
  if (!raw) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw;
  }
  if (Array.isArray(raw.voices)) {
    return raw.voices;
  }
  if (Array.isArray(raw.data)) {
    return raw.data;
  }
  return [];
};

const voiceId = (voice) => voice?.voice_id || voice?.id || voice?.voiceId || "";
const voiceLabel = (voice) => voice?.name || voice?.label || voiceId(voice);

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) {
    return "-";
  }
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${mins}:${secs}`;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) {
    return "-";
  }
  const date = new Date(timestamp * 1000);
  return date.toLocaleString();
};

const TopicAudioManager = ({ visible, onHide, course, token, toastRef }) => {
  const [topics, setTopics] = useState([]);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [courseContentUpdatedAt, setCourseContentUpdatedAt] = useState(null);
  const [isBulkGenerating, setBulkGenerating] = useState(false);
  const [audioGenerationAvailable, setAudioGenerationAvailable] = useState(true);
  const [audioGenerationMessage, setAudioGenerationMessage] = useState("");

  const voiceOptions = useMemo(
    () => voices.map((v) => ({ label: voiceLabel(v), value: voiceId(v) })),
    [voices]
  );

  const voiceLookup = useMemo(() => {
    const map = {};
    voices.forEach((voice) => {
      map[voiceId(voice)] = voiceLabel(voice);
    });
    return map;
  }, [voices]);

  const headers = useMemo(() => {
    if (!token) {
      return null;
    }
    return {
      Authorization: `Bearer ${token}`,
      "X-EDITOR": "nanoms",
    };
  }, [token]);

  const fetchTopicAudio = useCallback(
    async (topicId) => {
      if (!headers) {
        return { has_audio: false };
      }
      try {
        const resp = await axios.get(`/api/topics/${topicId}/audio`, { headers });
        return resp.data;
      } catch (audioErr) {
        if (audioErr?.response?.data) {
          return audioErr.response.data;
        }
        console.error(`Failed to load audio for topic ${topicId}`, audioErr);
        return { has_audio: false };
      }
    },
    [headers]
  );

  const fetchCourseTopics = useCallback(async () => {
    if (!headers || !course?.id) {
      return [];
    }

    const perPage = 100; // API caps per_page at 100, so paginate manually
    let page = 1;
    const aggregated = [];

    while (true) {
      const response = await axios.get(`/api/courses/${course.id}/topics`, {
        headers,
        params: { page, per_page: perPage },
      });

      const batch = response.data?.items || [];
      aggregated.push(...batch);

      const meta = response.data?._meta;
      if (!meta || page >= meta.total_pages) {
        break;
      }
      page += 1;
    }

    return aggregated.sort((a, b) => (a.position_id || 0) - (b.position_id || 0));
  }, [course?.id, headers]);

  const fetchCourseDetails = useCallback(async () => {
    if (!headers || !course?.id) {
      return null;
    }
    const response = await axios.get(`/api/courses/${course.id}`, { headers });
    return response.data;
  }, [course?.id, headers]);

  const fetchAudioGenerationStatus = useCallback(async () => {
    if (!headers) {
      return { available: false, message: "" };
    }
    try {
      const response = await axios.get("/api/audio/status", { headers });
      return response.data;
    } catch (err) {
      console.error("Failed to fetch audio generation status", err);
      return { available: false, message: "Unable to check audio generation status" };
    }
  }, [headers]);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }
    if (!course?.id || !headers) {
      return undefined;
    }
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [courseTopics, voicesResponse, courseDetails, audioStatus] = await Promise.all([
          fetchCourseTopics(),
          axios.get(`/api/tts/voices`, { headers }),
          fetchCourseDetails(),
          fetchAudioGenerationStatus(),
        ]);

        if (cancelled) {
          return;
        }

        // Set audio generation availability status
        setAudioGenerationAvailable(audioStatus.available);
        setAudioGenerationMessage(audioStatus.message || "");

        const enrichedTopics = await Promise.all(
          courseTopics.map(async (topic) => ({
            ...topic,
            audioInfo: await fetchTopicAudio(topic.id),
            voiceOverride: "",
            isGenerating: false,
          }))
        );

        if (cancelled) {
          return;
        }

        const normalizedVoices = normalizeVoices(voicesResponse.data);
        const preferredVoiceId = "JBFqnCBsd6RMkjVDRZzb";
        const preferredVoice = normalizedVoices.find(
          (voice) => voiceId(voice) === preferredVoiceId
        );
        const firstTopicVoice = enrichedTopics.find(
          (topic) => topic.audioInfo?.audio_voice_id
        )?.audioInfo?.audio_voice_id;
        const defaultVoice =
          (preferredVoice ? voiceId(preferredVoice) : "") ||
          firstTopicVoice ||
          (normalizedVoices.length ? voiceId(normalizedVoices[0]) : "");

        // Content timestamp, not last_edited: metadata edits must not flag
        // audio as out of sync.
        setCourseContentUpdatedAt(
          courseDetails?.content_updated_at || courseDetails?.last_edited || null
        );

        setTopics(enrichedTopics);
        setVoices(normalizedVoices);
        if (defaultVoice) {
          setSelectedVoice((current) => current || defaultVoice);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load topics or voices", err);
          setError("Failed to load topics or voices. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [visible, course?.id, headers, fetchTopicAudio, fetchCourseTopics, fetchCourseDetails, fetchAudioGenerationStatus]);

  const refreshTopicAudio = useCallback(
    async (topicId) => {
      const audio = await fetchTopicAudio(topicId);
      setTopics((prev) =>
        prev.map((topic) =>
          topic.id === topicId
            ? {
                ...topic,
                audioInfo: audio,
              }
            : topic
        )
      );
    },
    [fetchTopicAudio]
  );

  const ensureFreshAudioUrl = useCallback(
    async (topicId) => {
      const topic = topics.find((item) => item.id === topicId);
      if (!topic?.audioInfo?.audio_url) {
        return;
      }
      const expiresIn = topic.audioInfo.expires_in;

      // If the URL will expire within the next 15 seconds, refresh it.
      if (typeof expiresIn === "number" && expiresIn <= 15) {
        await refreshTopicAudio(topicId);
      }
    },
    [refreshTopicAudio, topics]
  );

  const handleToast = useCallback((severity, summary, detail) => {
    if (toastRef?.current) {
      toastRef.current.show({ severity, summary, detail, life: 4000 });
    }
  }, [toastRef]);

  const applyVoiceToAllTopics = () => {
    if (!selectedVoice) {
      handleToast("warn", "Voice required", "Select a voice first.");
      return;
    }
    setTopics((prev) =>
      prev.map((topic) => ({
        ...topic,
        voiceOverride: selectedVoice,
      }))
    );
    handleToast(
      "info",
      "Voice applied",
      "Selected voice assigned to all topics."
    );
  };

  const resolveVoiceForTopic = useCallback(
    (topic) =>
      topic.voiceOverride || topic.audioInfo?.audio_voice_id || selectedVoice || "",
    [selectedVoice]
  );

  const triggerGeneration = async (topicId) => {
    const topic = topics.find((t) => t.id === topicId);
    if (!topic) {
      return;
    }
    const currentVersion = topic.audioInfo?.audio_version || 0;
    const expectedVersion = currentVersion + 1;
    if (topic.audioInfo?.has_audio) {
      const confirmed = window.confirm(
        "Regenerate audio for this topic? This will replace the current audio."
      );
      if (!confirmed) {
        return;
      }
    }
    const voiceToUse = resolveVoiceForTopic(topic);
    if (!voiceToUse) {
      handleToast(
        "warn",
        "Voice required",
        "Select a voice before generating audio."
      );
      return;
    }
    setTopics((prev) =>
      prev.map((t) => (t.id === topicId ? { ...t, isGenerating: true } : t))
    );
    try {
      const postHeaders = headers
        ? { ...headers, "Content-Type": "application/json" }
        : { "Content-Type": "application/json" };
      await axios.post(
        `/api/topics/${topicId}/audio/generate`,
        { voice_id: voiceToUse },
        { headers: postHeaders }
      );
      handleToast(
        "info",
        "Queued",
        `${topic.name} audio generation is queued.`
      );
      await pollForAudioReady(topicId, expectedVersion);
    } catch (err) {
      console.error("Failed to generate audio", err);
      handleToast(
        "error",
        "Generation failed",
        err?.response?.data?.message || "Unable to generate audio."
      );
      setTopics((prev) =>
        prev.map((t) =>
          t.id === topicId
            ? {
                ...t,
                isGenerating: false,
              }
            : t
        )
      );
    }
  };

  const pollForAudioReady = useCallback(
    async (topicId, expectedVersion, attempts = 0, options = {}) => {
      const maxAttempts = options.maxAttempts || 72; // ~6 minutes at 5s interval
      const intervalMs = options.intervalMs || 5000;
      const audio = await fetchTopicAudio(topicId);
      if (
        audio?.has_audio &&
        audio.audio_url &&
        (typeof audio.audio_version === "number"
          ? audio.audio_version >= expectedVersion
          : expectedVersion === 1)
      ) {
        setTopics((prev) =>
          prev.map((topic) =>
            topic.id === topicId
              ? {
                ...topic,
                audioInfo: audio,
                isGenerating: false,
              }
              : topic
          )
        );
        handleToast(
          "success",
          "Audio ready",
          "Audio has been generated successfully."
        );
        return true;
      }
      if (attempts >= maxAttempts) {
        handleToast(
          "warn",
          "Still processing",
          "Audio generation is taking longer than expected."
        );
        setTopics((prev) =>
          prev.map((t) =>
            t.id === topicId ? { ...t, isGenerating: false } : t
          )
        );
        return false;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      return pollForAudioReady(topicId, expectedVersion, attempts + 1, options);
    },
    [fetchTopicAudio, handleToast]
  );

  const refreshAllStatuses = useCallback(async () => {
    if (!topics.length) {
      return;
    }
    await Promise.all(topics.map((topic) => refreshTopicAudio(topic.id)));
    handleToast("info", "Statuses refreshed", "Status data updated.");
  }, [refreshTopicAudio, topics, handleToast]);

  const triggerGenerationForAll = useCallback(async () => {
    if (!topics.length) {
      return;
    }
    if (!selectedVoice) {
      handleToast("warn", "Voice required", "Select a voice first.");
      return;
    }
    setBulkGenerating(true);
    try {
      const orderedTopics = [...topics].sort(
        (a, b) => (a.position_id || 0) - (b.position_id || 0)
      );
      let completedCount = 0;
      const postHeaders = headers
        ? { ...headers, "Content-Type": "application/json" }
        : { "Content-Type": "application/json" };

      const expectedVersions = new Map();
      orderedTopics.forEach((topic) => {
        const currentVersion = topic.audioInfo?.audio_version || 0;
        expectedVersions.set(topic.id, currentVersion + 1);
      });

      setTopics((prev) =>
        prev.map((topic) =>
          expectedVersions.has(topic.id)
            ? { ...topic, isGenerating: true }
            : topic
        )
      );

      await Promise.all(
        orderedTopics.map((topic) => {
          const voiceToUse = resolveVoiceForTopic(topic) || selectedVoice;
          return axios.post(
            `/api/topics/${topic.id}/audio/generate`,
            { voice_id: voiceToUse },
            { headers: postHeaders }
          );
        })
      );

      const results = await Promise.all(
        orderedTopics.map((topic) =>
          pollForAudioReady(topic.id, expectedVersions.get(topic.id), 0, {
            intervalMs: 10000,
            maxAttempts: 90, // ~15 minutes at 10s interval
          })
        )
      );

      completedCount = results.filter(Boolean).length;
      if (completedCount === orderedTopics.length) {
        handleToast(
          "success",
          "Audio generation complete",
          "All topics have been processed."
        );
      } else {
        handleToast(
          "warn",
          "Audio generation incomplete",
          "Some topics are still processing."
        );
      }
    } catch (err) {
      console.error("Bulk generation failed", err);
      handleToast(
        "error",
        "Bulk generation failed",
        err?.response?.data?.message || "Unable to generate audio."
      );
    } finally {
      setBulkGenerating(false);
    }
  }, [topics, selectedVoice, headers, pollForAudioReady, handleToast, resolveVoiceForTopic]);

  const renderTopicRow = (topic) => {
    const audio = topic.audioInfo || {};
    const hasAudio = Boolean(audio.audio_url);
    const audioVoiceName = voiceLookup[audio.audio_voice_id] || audio.audio_voice_id || "-";
    const outOfSync =
      hasAudio &&
      courseContentUpdatedAt &&
      audio.audio_generated_at &&
      audio.audio_generated_at < courseContentUpdatedAt;
    const statusLabel = hasAudio
      ? [
          "Voice: " + audioVoiceName,
          "Duration: " +
            (audio.audio_duration
              ? formatDuration(audio.audio_duration)
              : "-"),
          "Updated: " + formatTimestamp(audio.audio_generated_at),
          outOfSync ? "Out of sync" : null,
        ].filter(Boolean).join(" | ")
      : "No audio yet";

    return (
      <div key={topic.id} className="audio-topic-row">
        <Tooltip target={`.audio-topic-action-${topic.id}`} />
        <div className="audio-topic-main">
          <div className="audio-topic-title">
            <span className="audio-topic-name">{topic.name}</span>
            <span className="audio-topic-meta">#{topic.position_id}</span>
          </div>
          <div className="audio-topic-status">{statusLabel}</div>
          <div className="audio-topic-controls">
            <Button
              variant="secondary"
              disabled={topic.isGenerating || !audioGenerationAvailable}
              onClick={() => triggerGeneration(topic.id)}
              className={`audio-topic-action audio-topic-action-${topic.id}`}
              title={!audioGenerationAvailable ? audioGenerationMessage : ""}
              data-pr-tooltip={!audioGenerationAvailable ? audioGenerationMessage : ""}
              data-pr-position="top"
            >
              {topic.isGenerating
                ? "Generating..."
                : hasAudio
                ? "Regenerate"
                : "Generate"}
            </Button>
            {topic.isGenerating && (
              <div className="audio-topic-spinner">
                <ProgressSpinner
                  style={{ width: "28px", height: "28px" }}
                  strokeWidth="6"
                />
              </div>
            )}
          </div>
        </div>
        <div className="audio-topic-player">
          {hasAudio ? (
            <audio
              controls
              src={audio.audio_url}
              preload="none"
              onPlay={() => ensureFreshAudioUrl(topic.id)}
            >
              Your browser does not support the audio element.
            </audio>
          ) : (
            <span className="audio-topic-placeholder">
              Generate audio to enable playback.
            </span>
          )}
        </div>
      </div>
    );
  };

  const closeAndReset = useCallback(() => {
    setTopics([]);
    onHide();
  }, [onHide]);

  const dialogBody = () => {
    if (loading) {
      return (
        <div className="audio-manager-loading">
          <ProgressSpinner />
          <p>Loading topics and voices...</p>
        </div>
      );
    }

    if (error) {
      return <p className="audio-manager-error">{error}</p>;
    }

    if (!topics.length) {
      return (
        <div className="audio-manager-empty">
          <p>No topics found for this course yet.</p>
        </div>
      );
    }

    return (
      <div className="audio-manager-content">
        <Tooltip target=".audio-generation-disabled" />
        <div className="audio-manager-toolbar">
          <Dropdown
            value={selectedVoice}
            options={voiceOptions}
            onChange={(e) => setSelectedVoice(e.value)}
            placeholder="Select voice for course"
            className="audio-voice-dropdown"
          />
          <Button
            variant="outline-primary"
            disabled={!selectedVoice || !audioGenerationAvailable}
            onClick={applyVoiceToAllTopics}
            className={!audioGenerationAvailable ? "audio-generation-disabled" : ""}
            title={!audioGenerationAvailable ? audioGenerationMessage : ""}
            data-pr-tooltip={!audioGenerationAvailable ? audioGenerationMessage : ""}
            data-pr-position="top"
          >
            Apply voice to all topics
          </Button>
          <Button
            variant="primary"
            disabled={!selectedVoice || isBulkGenerating || !audioGenerationAvailable}
            onClick={triggerGenerationForAll}
            className={!audioGenerationAvailable ? "audio-generation-disabled" : ""}
            title={!audioGenerationAvailable ? audioGenerationMessage : ""}
            data-pr-tooltip={!audioGenerationAvailable ? audioGenerationMessage : ""}
            data-pr-position="top"
          >
            {isBulkGenerating ? "Generating all..." : "Generate all topics"}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={refreshAllStatuses}
            disabled={!topics.length}
          >
            Refresh statuses
          </Button>
        </div>
        <div className="audio-topics-list">
          {topics.map((topic) => renderTopicRow(topic))}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      visible={visible}
      header={`Audio for ${course?.name || "course"}`}
      modal
      style={{ width: "80vw", maxWidth: "1100px" }}
      breakpoints={{ "960px": "95vw" }}
      className="topic-audio-dialog"
      onHide={closeAndReset}
    >
      {dialogBody()}
    </Dialog>
  );
};

TopicAudioManager.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  course: PropTypes.object,
  token: PropTypes.string,
  toastRef: PropTypes.object,
};

TopicAudioManager.defaultProps = {
  course: null,
  token: "",
  toastRef: null,
};

export default TopicAudioManager;
